import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { endpoints } from "../../api/endpoints";

export interface ProfileProperty {
  id: number;
  label: string;
  keyName: string;
  /** Empty for free-text properties, which have no option list. */
  apiUrl: string;
  isMandatory: boolean;
  otherAllowed: boolean;
}

export interface PropertyOption {
  id: number;
  label: string;
  allowInput?: boolean;
  displayOrder?: number | null;
  status?: string;
  keyName?: string;
}

export function useProfileProperties() {
  return useQuery({
    queryKey: ["profile-properties"],
    queryFn: async () => {
      const { data } = await api.get<ProfileProperty[]>(endpoints.metas.profileProperties());
      return data;
    },
  });
}

export function usePropertyOptions(apiUrl: string) {
  return useQuery({
    queryKey: ["property-options", apiUrl],
    queryFn: async () => {
      const { data } = await api.get<PropertyOption[]>(endpoints.metas.dynamic(apiUrl));
      return Array.isArray(data) ? data : [];
    },
    enabled: apiUrl !== "",
  });
}

function useInvalidateOptions(apiUrl: string) {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["property-options", apiUrl] });
}

export function useAddOption(apiUrl: string) {
  const invalidate = useInvalidateOptions(apiUrl);
  return useMutation({
    mutationFn: async (option: { label: string; allowInput: boolean }) => {
      const { data } = await api.post(endpoints.metas.dynamic(apiUrl), option);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateOption(apiUrl: string) {
  const invalidate = useInvalidateOptions(apiUrl);
  return useMutation({
    mutationFn: async (option: PropertyOption) => {
      const { data } = await api.put(
        endpoints.metas.dynamicById(apiUrl, String(option.id)),
        option,
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteOption(apiUrl: string) {
  const invalidate = useInvalidateOptions(apiUrl);
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(endpoints.metas.dynamicById(apiUrl, String(id)));
      return data;
    },
    onSuccess: invalidate,
  });
}

/** Reorders a property's options; the server takes the ids in their new order. */
export function useReorderOptions(propertyId: number, keyName: string, apiUrl: string) {
  const invalidate = useInvalidateOptions(apiUrl);
  return useMutation({
    mutationFn: async (orderedIds: number[]) => {
      const { data } = await api.put(endpoints.metas.profilePropertyOrder(String(propertyId)), {
        keyName,
        profileOptionsOrder: orderedIds,
      });
      return data;
    },
    onSuccess: invalidate,
  });
}
